import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
} from "@ant-design/icons";
import { Button, Card, Col, Row, Spin, Statistic } from "antd";
import moment from "moment";
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
} from "recharts";

import { useEndTime, useStartTime } from "../DateContext/DateContext";
import useActiveMemberMetrics from "./useActiveMemberMetrics";
import useMessageMetrics from "./useMessageMetrics";
import useReactionMetrics from "./useReactionMetrics";
import useWeeklyMessageCount from "./useWeeklyMessageCount";

// :::: Component Props ::::
// url: The url for the "View More" button. Don't pass url if no button is to be shown.
// teamId: The team ID
// span: The component displays a grid. Span specifies the column width.
export default function EngagementCard(props) {
  const { url, teamId, span } = props;
  const platform = localStorage.getItem("platform");
  const { startTime, setStartTime } = useStartTime();
  const { endTime, setEndTime } = useEndTime();
  const navigate = useNavigate();

  const { activeMemberMetrics, loading: loadingActive } =
    useActiveMemberMetrics(startTime, endTime, teamId, platform);
  const { messageMetrics, loading: loadingMessages } = useMessageMetrics(
    startTime,
    endTime,
    teamId,
    platform
  );
  const { reactionMetrics, loading: loadingReactions } = useReactionMetrics(
    startTime,
    endTime,
    teamId,
    platform
  );
  const { weeklyMessageCount, loading: loadingWeekly } = useWeeklyMessageCount(
    endTime,
    teamId,
    platform
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="custom-tooltip"
          style={{ background: "rgba(76, 175, 80, 0.3)" }}
        >
          <p className="label">
            {`Starting ${label} : ${payload[0].value} `}
            {payload[0].payload.label ? "(Ongoing)" : ""}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Spin
      spinning={
        loadingActive || loadingMessages || loadingReactions || loadingWeekly
      }
    >
      <Card
        title="Engagement "
        bordered={false}
        className="card-engagement"
        extra={
          url ? (
            <Button type="link" onClick={() => navigate(url)}>
              View more
            </Button>
          ) : null
        }
      >
        <Row>
          <Col span={span}>
            <Statistic
              title="Messages posted"
              value={messageMetrics.currentMessage}
              precision={0}
              loading={messageMetrics.loading}
              suffix={
                <p
                  style={{
                    fontSize: 16,
                    color:
                      messageMetrics.messagePercent > 0 ? "#3f8600" : "#cf1322",
                  }}
                >
                  {messageMetrics.messagePercent === "Infinity" && <>{null}</>}
                  {messageMetrics.messagePercent > 0 && (
                    <>
                      (<CaretUpOutlined style={{ color: "rgb(63, 134, 0)" }} />
                      {messageMetrics.messagePercent}%)
                    </>
                  )}
                  {messageMetrics.messagePercent < 0 && (
                    <>
                      (
                      <CaretDownOutlined style={{ color: "#cf1322" }} />
                      {messageMetrics.messagePercent}%)
                    </>
                  )}
                  {null}
                </p>
              }
            />
            <p>
              {messageMetrics.previousMessage === 0 ? (
                <>{null}</>
              ) : (
                <>(Previous: {messageMetrics.previousMessage} messages)</>
              )}
            </p>
          </Col>
          <Col span={span}>
            <Statistic
              title="Active members"
              value={activeMemberMetrics.currentActive}
              precision={0}
              loading={activeMemberMetrics.loading}
              suffix={
                <p
                  style={{
                    fontSize: 16,
                    color:
                      activeMemberMetrics.activePercent > 0
                        ? "#3f8600"
                        : "#cf1322",
                  }}
                >
                  {activeMemberMetrics.activePercent === "Infinity" && (
                    <>{null}</>
                  )}
                  {activeMemberMetrics.activePercent > 0 && (
                    <>
                      (<CaretUpOutlined style={{ color: "#3f8600" }} />
                      {activeMemberMetrics.activePercent}%)
                    </>
                  )}
                  {activeMemberMetrics.activePercent < 0 && (
                    <>
                      (
                      <CaretDownOutlined style={{ color: "#cf1322" }} />
                      {activeMemberMetrics.activePercent}%)
                    </>
                  )}
                  {null}
                </p>
              }
            />
            <p>
              {activeMemberMetrics.previousActive === 0 ? (
                <>{null}</>
              ) : (
                <>(Previous: {activeMemberMetrics.previousActive} members)</>
              )}
            </p>
          </Col>
          <Col span={span}>
            <Statistic
              title="Reactions added"
              value={reactionMetrics.currentReactions}
              precision={0}
              loading={reactionMetrics.loading}
              suffix={
                <p
                  style={{
                    fontSize: 16,
                    color:
                      reactionMetrics.reactionsPercent > 0
                        ? "#3f8600"
                        : "#cf1322",
                  }}
                >
                  {reactionMetrics.reactionsPercent === "Infinity" && (
                    <>{null}</>
                  )}
                  {reactionMetrics.reactionsPercent > 0 && (
                    <>
                      (<CaretUpOutlined style={{ color: "rgb(63, 134, 0)" }} />
                      {reactionMetrics.reactionsPercent}%)
                    </>
                  )}
                  {reactionMetrics.reactionsPercent < 0 && (
                    <>
                      (
                      <CaretDownOutlined style={{ color: "#cf1322" }} />
                      {reactionMetrics.reactionsPercent}%)
                    </>
                  )}
                  {null}
                </p>
              }
            />
            <p>
              {reactionMetrics.previousReactions === 0 ? (
                <>{null}</>
              ) : (
                <>(Previous: {reactionMetrics.previousReactions} reactions)</>
              )}
            </p>
          </Col>
          <Col span={span}>
            <p style={{ color: "rgba(0, 0, 0, 0.45)" }}>Weekly messages sent</p>
            <div style={{ width: "100%", height: "100px", paddingRight: 30 }}>
              <ResponsiveContainer>
                <LineChart data={weeklyMessageCount}>
                  <XAxis
                    dataKey="week"
                    tickFormatter={(date) => moment(date).format("MM/DD")}
                  />
                  <ReTooltip content={CustomTooltip} />
                  <Line
                    type="monotone"
                    dataKey="total_count"
                    stroke="#8884d8"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="current"
                    stroke="#8884d8"
                    strokeWidth={2}
                    strokeDasharray="3 4 5 2"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Col>
        </Row>
      </Card>
    </Spin>
  );
}
